import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { ShoppingBag, User, Menu, X, Home, Search, Heart, ChevronDown, Store } from 'lucide-react';
import { useState } from 'react';
import { Button } from './ui/button';
import { NotificationBanner } from './NotificationComponents';
import { ActiveOrderBanner } from './ActiveOrderBanner';
import { LanguageSwitcher } from './LanguageSwitcher';
import { SkipLink } from './ui/accessible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

import { useTranslation } from 'react-i18next';

const tradeLinks = [
  { name: 'Restaurants', search: 'Restaurant' },
  { name: 'Boulangeries', search: 'Boulangerie' },
  { name: 'Boucheries', search: 'Boucherie' },
  { name: 'Primeurs', search: 'Primeur' },
  { name: 'Fromageries', search: 'Fromagerie' },
  { name: 'Poissonneries', search: 'Poissonnerie' },
];

export const Header = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { itemCount } = useCart();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
      <SkipLink />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="LEMO" className="w-20 h-20 -my-4 object-contain" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-5">
            {/* Commerces Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-gray-600 hover:text-emerald-500 font-medium transition-colors outline-none">
                Commerces
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/restaurants')}>
                  Tous les commerces
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {tradeLinks.map((trade) => (
                  <DropdownMenuItem
                    key={trade.name}
                    onClick={() => navigate(`/restaurants?search=${encodeURIComponent(trade.search)}`)}
                  >
                    {trade.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Link
              to="/restaurant-signup"
              className="text-emerald-600 hover:text-emerald-700 font-medium transition-colors flex items-center gap-1.5"
            >
              <Store className="w-4 h-4" />
              Espace Pro
            </Link>
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <LanguageSwitcher />

            {isAuthenticated && user?.role === 'Client' && (
              <Link 
                to="/checkout" 
                className="relative p-2 hover:bg-gray-100 rounded-full transition-colors"
                data-testid="cart-button"
              >
                <ShoppingBag className="w-6 h-6 text-gray-700" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </Link>
            )}

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-full transition-colors" data-testid="user-menu">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                      <User className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="hidden sm:block text-sm font-medium text-gray-700">{user?.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {user?.role === 'Client' && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/mon-compte')}>
                        {t('nav.myAccount')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/favoris')}>
                        {t('nav.myFavorites')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/mon-compte?tab=orders')}>
                        {t('nav.myOrders')}
                      </DropdownMenuItem>
                    </>
                  )}
                  {user?.role === 'Restaurant' && (
                    <DropdownMenuItem onClick={() => navigate('/restaurant-dashboard')}>
                      {t('nav.dashboard')}
                    </DropdownMenuItem>
                  )}
                  {user?.role === 'Admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin')}>
                      {t('nav.admin')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')}
                  className="hidden sm:flex"
                  data-testid="login-button"
                >
                  {t('nav.login')}
                </Button>
                <Button 
                  onClick={() => navigate('/signup')}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full"
                  data-testid="signup-button"
                >
                  {t('nav.signup')}
                </Button>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <button 
              className="md:hidden p-2 hover:bg-gray-100 rounded-lg"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-100 animate-slide-up">
            <nav className="flex flex-col gap-1">
              <Link 
                to="/restaurants" 
                className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Tous les commerces
              </Link>
              {tradeLinks.map((trade) => (
                <Link
                  key={trade.name}
                  to={`/restaurants?search=${encodeURIComponent(trade.search)}`}
                  className="px-4 py-2.5 pl-8 text-gray-600 hover:bg-gray-50 rounded-lg text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {trade.name}
                </Link>
              ))}
              <div className="border-t border-gray-100 my-1" />
              <Link 
                to="/restaurant-signup" 
                className="px-4 py-3 text-emerald-600 hover:bg-emerald-50 rounded-lg font-medium flex items-center gap-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Store className="w-4 h-4" />
                Espace Pro
              </Link>
              {!isAuthenticated && (
                <Link 
                  to="/login" 
                  className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Connexion
                </Link>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export const MobileNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { itemCount } = useCart();
  const { t } = useTranslation();
  
  if (!isAuthenticated || user?.role !== 'Client') return null;

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 pb-safe pt-2 px-4 flex justify-around items-center z-50 md:hidden">
      <button
        onClick={() => navigate('/')}
        className={`flex flex-col items-center gap-1 p-2 ${isActive('/') ? 'text-emerald-500' : 'text-gray-500'}`}
      >
        <Home className="w-5 h-5" />
        <span className="text-xs font-medium">{t('nav.home')}</span>
      </button>
      <button
        onClick={() => navigate('/restaurants')}
        className={`flex flex-col items-center gap-1 p-2 ${isActive('/restaurants') ? 'text-emerald-500' : 'text-gray-500'}`}
      >
        <Search className="w-5 h-5" />
        <span className="text-xs font-medium">{t('nav.explore')}</span>
      </button>
      <button
        onClick={() => navigate('/favoris')}
        className={`flex flex-col items-center gap-1 p-2 ${isActive('/favoris') ? 'text-emerald-500' : 'text-gray-500'}`}
      >
        <Heart className="w-5 h-5" />
        <span className="text-xs font-medium">{t('nav.favorites')}</span>
      </button>
      <button
        onClick={() => navigate('/checkout')}
        className={`flex flex-col items-center gap-1 p-2 relative ${isActive('/checkout') ? 'text-emerald-500' : 'text-gray-500'}`}
      >
        <ShoppingBag className="w-5 h-5" />
        {itemCount > 0 && (
          <span className="absolute top-0 right-0 w-4 h-4 bg-emerald-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {itemCount}
          </span>
        )}
        <span className="text-xs font-medium">{t('nav.cart')}</span>
      </button>
      <button
        onClick={() => navigate('/mon-compte')}
        className={`flex flex-col items-center gap-1 p-2 ${isActive('/mon-compte') ? 'text-emerald-500' : 'text-gray-500'}`}
      >
        <User className="w-5 h-5" />
        <span className="text-xs font-medium">{t('nav.account')}</span>
      </button>
    </nav>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white py-12 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <img src="/logo.png" alt="LEMO" className="w-20 h-20 -my-4 object-contain" />
            </div>
            <p className="text-gray-400 text-sm">
              La marketplace click & collect pour vos commerces de proximité.
            </p>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Commerces</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/restaurants" className="hover:text-white transition-colors">Tous les commerces</Link></li>
              <li><Link to="/restaurants?search=Restaurant" className="hover:text-white transition-colors">Restaurants</Link></li>
              <li><Link to="/restaurants?search=Boulangerie" className="hover:text-white transition-colors">Boulangeries</Link></li>
              <li><Link to="/restaurants?search=Boucherie" className="hover:text-white transition-colors">Boucheries</Link></li>
              <li><Link to="/restaurants?search=Primeur" className="hover:text-white transition-colors">Primeurs</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Professionnels</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/restaurant-signup" className="hover:text-white transition-colors">Devenir partenaire</Link></li>
              <li><Link to="/restaurant-signup" className="hover:text-white transition-colors">Espace Pro</Link></li>
              <li><Link to="/cgu" className="hover:text-white transition-colors">CGU</Link></li>
              <li><Link to="/cgv" className="hover:text-white transition-colors">CGV</Link></li>
              <li><Link to="/politique-de-confidentialite" className="hover:text-white transition-colors">Confidentialité</Link></li>
              <li><Link to="/mentions-legales" className="hover:text-white transition-colors">Mentions légales</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Contact</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li>m.duclos@petites-natures.fr</li>
              <li>06 89 28 39 90</li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
          &copy; {new Date().getFullYear()} LEMO. Tous droits réservés.
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children, showFooter = true }) => {
  const { user, isAuthenticated } = useAuth();
  
  return (
    <div className="min-h-screen flex flex-col">
      {/* Active Order Banner - show when client has an active order */}
      <ActiveOrderBanner />
      {/* Notification Banner for clients */}
      {isAuthenticated && user?.role === 'Client' && <NotificationBanner />}
      <Header />
      <main id="main-content" role="main" className={`flex-1 ${user?.role === 'Client' ? 'pb-20 md:pb-0' : ''}`}>
        {children}
      </main>
      {showFooter && <Footer />}
      <MobileNav />
    </div>
  );
};
